import { isNil } from 'lodash';
import { StyleSheet } from 'react-native';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { CardBlockScrollProvider } from '@shared/components/blocks/contexts/scroll';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBCardDetailsOptionsButton } from '@shared/scenes/cards/details/containers/optionsButton';
import { ExternalBatchContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import {
  ResponsiveFormProvider,
  useResponsiveFormContext,
} from '@shared/scenes/entity/components/ResponsiveFormContext';
import { CardEntityEditForm } from '@shared/scenes/entity/edit/components/CardEntityEditForm';
import { useEditCardEntity } from '@shared/scenes/entity/edit/hooks/useEditCardEntity';
import { CREATE_MODAL_FIXED_WIDTH, EXTERNAL_FORM_MAX_WIDTH, grid } from '@shared/styles';
import { useChannel } from '@shared/util/hooks';
import styles from './ReadOnlyCardEntityForm.module.css';

export function ReadOnlyCardEntityForm({
  channelId,
  templateSlug,
  entityId,
}: {
  channelId: number;
  templateSlug: string;
  entityId: string;
}) {
  const channel = useChannel({ channelId });
  const workflowTemplateId = channel?.workflowTemplateId;
  const channelSlug = channel?.slug;
  if (isNil(channelSlug)) {
    return <MBLoadingIndicator fill />;
  }

  return (
    <CardBlockScrollProvider>
      <ExternalBatchContextProvider>
        <ResponsiveFormProvider>
          <Form
            workflowTemplateId={workflowTemplateId ?? undefined}
            templateSlug={templateSlug}
            entityId={entityId}
            channelSlug={channelSlug}
          />
        </ResponsiveFormProvider>
      </ExternalBatchContextProvider>
    </CardBlockScrollProvider>
  );
}

function Form({
  workflowTemplateId,
  templateSlug,
  entityId,
  channelSlug,
}: {
  workflowTemplateId?: string;
  templateSlug: string;
  entityId: string;
  channelSlug: string;
}) {
  const isResponsiveForms = useResponsiveFormContext();

  const { components, fields, updateField, isLoading, viewTemplate, viewTemplateTitle } =
    useEditCardEntity({
      entityId,
      workflowTemplateId,
      viewTemplateId: templateSlug,
      isReadOnly: true,
    });

  return (
    <View className={[styles.container, isResponsiveForms && styles.containerResponsive]}>
      {isLoading && <MBLoadingIndicator fill />}
      {!isLoading && (
        <>
          <View className={[styles.header, isResponsiveForms && styles.headerResponsive]}>
            <View
              row
              centerVertically
              className={[
                styles.headerContent,
                !isResponsiveForms && styles.headerBorder,
                isResponsiveForms && styles.headerContentResponsive,
              ]}
            >
              <Text size="h4" numberOfLines={1} className={styles.headerTitle}>
                {viewTemplateTitle}
              </Text>
              <View className={styles.optionsButton}>
                <MBCardDetailsOptionsButton
                  channelSlug={channelSlug}
                  cardId={entityId}
                  hiddenCardOptions={[
                    'edit',
                    'duplicate',
                    'share',
                    'cardHistory',
                    'print',
                    'selectMultiple',
                    'delete',
                    'bulkUpdate',
                  ]}
                />
              </View>
            </View>
          </View>
          <View className={styles.form}>
            <CardEntityEditForm
              components={components}
              fields={fields}
              updateField={updateField}
              errors={[]}
              workflowTemplateId={workflowTemplateId}
              viewTemplateId={viewTemplate?.id}
              entityId={entityId}
              style={[
                formStyles.formContent,
                isResponsiveForms && formStyles.formContentResponsive,
              ]}
            />
          </View>
        </>
      )}
    </View>
  );
}

const formStyles = StyleSheet.create({
  formContent: {
    width: '100%',
    maxWidth: EXTERNAL_FORM_MAX_WIDTH,
    alignSelf: 'center',
    paddingBottom: grid(8),
  },
  formContentResponsive: {
    maxWidth: CREATE_MODAL_FIXED_WIDTH,
  },
});
